import React, { useEffect, useState } from "react";
import {
  Campaign,
  CampaignLabelingJob,
  CampaignPrefilteringJob,
} from "../../appstore/models";
import {
  Checkbox,
  Header,
  Container,
  DateRangePicker,
  DateRangePickerProps,
} from "@awsui/components-react";

import {
  ContentSection,
  ErrorMessage,
  SelectCampaignLabelingJob,
  SelectCampaignPrefilteringJob,
} from "../shared/Components";
import Map from "./map/Map";
import RasterLayer from "./map/RasterLayer";
import VectorLayer, { TileLayerMetadata } from "./map/VectorLayer";
import MapPopup from "./map/MapPopup";
import maplibregl from "maplibre-gl";
import styled from "styled-components";

import "rc-slider/assets/index.css";
import "rc-tooltip/assets/bootstrap.css";
import Slider, { createSliderWithTooltip } from "rc-slider";
import theme from "../../common/theme";
import moment from "moment";
import { s3UrlToHttpsUrl, getSignedS3UrlFromS3Uri } from "../../common/helpers";

const mapCenter: [number, number] = [-122.3439487, 47.6074987];
const styleUrl =
  process.env.REACT_APP_MAP_URL ||
  "https://api.maptiler.com/maps/streets/style.son?key=YOUR_KEY_HERE";

const Range = createSliderWithTooltip(Slider.Range);
const CampaignMapContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const MapContainer = styled.div<{ height?: number }>`
  flex-grow: 1;
  min-height: ${(p) => (p.height ? p.height : 500)}px;
`;

const MapSidebarContainer = styled.div<{
  collapsed?: boolean;
  height?: number;
}>`
  display: flex;
  flex-direction: column;
  ${(p) =>
    p.collapsed
      ? `
      height:${p.height ? p.height : 500}px;
      >div {
        height: 100%;
      }
    `
      : "width: 225px;"}
  margin-right:2rem;
`;

const Filter = styled.div`
  margin-top: 0.5rem;
  margin-bottom: 1rem;
`;

const FilterTitle = styled.div``;

const SidebarHeader = styled.div`
  font-size: 1.75rem;

  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid ${theme.grey300};
`;

const FilterContainer = styled.div`
  width: 100%;
  padding: 1rem 0rem;
  display: flex;
  align-items: center;
`;

const SliderContainer = styled(FilterContainer)`
  div.rc-slider {
    margin: 0 1.25rem;
  }
`;

const DateRangeFilterContainer = styled.div`
  margin-top: 0.5rem;
  > div > div > div > div {
    min-width: 100% !important;
    > button > span {
      width: 100%;
      word-wrap: anywhere;
    }
  }
`;

const RangeTextInput = styled.input<{
  error?: boolean;
}>`
  width: 40px;
  ${(p) =>
    p.error
      ? `
        border-left:3px solid ${theme.red600};
        border-top: 1px solid ${theme.red600};
        border-right: 1px solid ${theme.red600};
        border-bottom: 1px solid ${theme.red600};
        color: ${theme.red600};
      `
      : null}
`;

const isValidRangeText = (text: string) => {
  const val = Number(text);
  return val <= 1 && val >= 0;
};

const DateRangeFilter = (props: {
  annotationStartDate: string | null;
  setAnnotationStartDate: (date: string | null) => void;
  annotationEndDate: string | null;
  setAnnotationEndDate: (date: string | null) => void;
}) => {
  const { setAnnotationStartDate, setAnnotationEndDate } = props;
  const [value, setValue] = React.useState<DateRangePickerProps.Value | null>(
    null
  );
  const setDate = (detail: DateRangePickerProps.ChangeDetail) => {
    setValue(detail.value);
    if (!detail) {
      setAnnotationStartDate(null);
      setAnnotationEndDate(null);
      return;
    }
    if (detail.value?.type === "absolute") {
      setAnnotationStartDate(detail.value.startDate);
      setAnnotationEndDate(detail.value.endDate);
      return;
    } else if (detail.value?.type === "relative") {
      setAnnotationStartDate(
        moment().subtract(detail.value.amount, detail.value.unit).toISOString()
      );
      setAnnotationEndDate(moment().toISOString());
    }
  };
  return (
    <DateRangeFilterContainer>
      <DateRangePicker
        isValidRange={(value) => {
          return {
            valid: true,
          };
        }}
        onChange={({ detail }) => setDate(detail)}
        value={value}
        relativeOptions={[
          {
            key: "previous-5-minutes",
            amount: 5,
            unit: "minute",
            type: "relative",
          },
          {
            key: "previous-30-minutes",
            amount: 30,
            unit: "minute",
            type: "relative",
          },
          {
            key: "previous-1-hour",
            amount: 1,
            unit: "hour",
            type: "relative",
          },
          {
            key: "previous-6-hours",
            amount: 6,
            unit: "hour",
            type: "relative",
          },
        ]}
        i18nStrings={{
          todayAriaLabel: "Today",
          nextMonthAriaLabel: "Next month",
          previousMonthAriaLabel: "Previous month",
          customRelativeRangeDurationLabel: "Duration",
          customRelativeRangeDurationPlaceholder: "Enter duration",
          customRelativeRangeOptionLabel: "Custom range",
          customRelativeRangeOptionDescription:
            "Set a custom range in the past",
          customRelativeRangeUnitLabel: "Unit of time",
          formatRelativeRange: (e) => {
            const t = 1 === e.amount ? e.unit : e.unit + "s";
            return `Last ${e.amount} ${t}`;
          },
          formatUnit: (e, t) => (1 === t ? e : e + "s"),
          dateTimeConstraintText:
            "Range must be between 6 - 30 days. Use 24 hour format.",
          relativeModeTitle: "Relative range",
          absoluteModeTitle: "Absolute range",
          relativeRangeSelectionHeading: "Choose a range",
          startDateLabel: "Start date",
          endDateLabel: "End date",
          startTimeLabel: "Start time",
          endTimeLabel: "End time",
          clearButtonLabel: "Clear",
          cancelButtonLabel: "Cancel",
          applyButtonLabel: "Apply",
        }}
        placeholder="Filter by a date and time range"
      />
    </DateRangeFilterContainer>
  );
};

const MapSidebar = (props: {
  campaign: Campaign;
  height?: number;
  selectedCampaignLabelingJob: CampaignLabelingJob | undefined;
  onChangeCampaignLabelingJob: (
    campaignLabelingJob: CampaignLabelingJob | undefined
  ) => void;
  selectedCampaignPrefilteringJob: CampaignPrefilteringJob | undefined;
  onChangeCampaignPrefilteringJob: (
    campaignPrefilteringJob: CampaignPrefilteringJob | undefined
  ) => void;

  onChangePrefilteringConfidenceStart: (
    prefilteringConfidenceStart: number
  ) => void;
  onChangePrefilteringConfidenceEnd: (
    prefilteringConfidenceEnd: number
  ) => void;

  annotationConfidenceStart: number;
  setAnnotationConfidenceStart: (annotationConfidenceStart: number) => void;
  annotationConfidenceEnd: number;
  setAnnotationConfidenceEnd: (annotationConfidenceEnd: number) => void;
  preFilteringConfidenceStart: number;
  setPreFilteringConfidenceStart: (prefilteringConfidenceStart: number) => void;
  preFilteringConfidenceEnd: number;
  setPreFilteringConfidenceEnd: (preFilteringConfidenceEnd: number) => void;
  annotationStartDate: string | null;
  setAnnotationStartDate: (date: string | null) => void;
  annotationEndDate: string | null;
  setAnnotationEndDate: (date: string | null) => void;
  selectedClassTypes: Set<string>;
  setSelectedClassTypes: (selectedClassTypes: Set<string>) => void;
}) => {
  const {
    campaign,
    height,
    selectedCampaignLabelingJob,
    onChangeCampaignLabelingJob,
    selectedCampaignPrefilteringJob,
    onChangeCampaignPrefilteringJob,

    annotationConfidenceStart,
    setAnnotationConfidenceStart,

    annotationConfidenceEnd,
    setAnnotationConfidenceEnd,

    preFilteringConfidenceStart,
    setPreFilteringConfidenceStart,

    preFilteringConfidenceEnd,
    setPreFilteringConfidenceEnd,

    annotationStartDate,
    setAnnotationStartDate,
    annotationEndDate,
    setAnnotationEndDate,
    selectedClassTypes,
    setSelectedClassTypes,
  } = props;

  const classTypes = selectedCampaignLabelingJob?.FeatureClasses;

  const [annotationConfidenceStartText, setAnnotationConfidenceStartText] =
    useState("0");
  const [annotationConfidenceEndText, setAnnotationConfidenceEndText] =
    useState("1");

  const [preFilteringConfidenceStartText, setPrefilteringConfidenceStartText] =
    useState("0");
  const [preFilteringConfidenceEndText, setPrefilteringConfidenceEndText] =
    useState("1");

  useEffect(() => {
    setAnnotationConfidenceStartText(`${annotationConfidenceStart}`);
    setAnnotationConfidenceEndText(`${annotationConfidenceEnd}`);
  }, [annotationConfidenceStart, annotationConfidenceEnd]);
  useEffect(() => {
    setPrefilteringConfidenceStartText(`${preFilteringConfidenceStart}`);
    setPrefilteringConfidenceEndText(`${preFilteringConfidenceEnd}`);
  }, [preFilteringConfidenceStart, preFilteringConfidenceEnd]);

  function onAnnotationConfidenceChange(value: number[]) {
    setAnnotationConfidenceStart(value[0]);
    setAnnotationConfidenceEnd(value[1]);
  }
  function onPrefilteringConfidenceChange(value: number[]) {
    setPreFilteringConfidenceStart(value[0]);
    setPreFilteringConfidenceEnd(value[1]);
  }
  return (
    <MapSidebarContainer height={height}>
      <Container>
        <SidebarHeader>Filters </SidebarHeader>

        <Filter>
          <FilterTitle>Select Pre-filtering Job</FilterTitle>
          <FilterContainer>
            <SelectCampaignPrefilteringJob
              campaignJobName={campaign.JobName}
              selectedCampaignPrefilteringJob={selectedCampaignPrefilteringJob}
              onChange={(prefilteringJob) =>
                onChangeCampaignPrefilteringJob(prefilteringJob)
              }
            />
          </FilterContainer>
        </Filter>

        {selectedCampaignPrefilteringJob ? (
          <Filter>
            <FilterTitle>Pre-filtering Confidence</FilterTitle>
            <SliderContainer>
              <RangeTextInput
                type="text"
                error={!isValidRangeText(preFilteringConfidenceStartText)}
                value={preFilteringConfidenceStartText}
                onChange={(e) => {
                  setPrefilteringConfidenceStartText(e.currentTarget.value);
                  const value = Number(e.currentTarget.value);
                  if (isNaN(value)) {
                    return;
                  }
                  setPreFilteringConfidenceStart(value);
                }}
              />
              <Range
                min={0}
                max={1}
                value={[preFilteringConfidenceStart, preFilteringConfidenceEnd]}
                defaultValue={[0, 1]}
                tipFormatter={(value) => value}
                step={0.01}
                trackStyle={[{ backgroundColor: theme.blue600 }]}
                handleStyle={[{ borderColor: theme.blue600 }]}
                onChange={onPrefilteringConfidenceChange}
              />
              <RangeTextInput
                type="text"
                value={preFilteringConfidenceEndText}
                onChange={(e) => {
                  const value = Number(e.currentTarget.value);
                  if (isNaN(value)) {
                    return;
                  }
                  setPreFilteringConfidenceEnd(value);
                }}
              />
            </SliderContainer>
            {!isValidRangeText(preFilteringConfidenceStartText) ||
            !isValidRangeText(preFilteringConfidenceEndText) ? (
              <ErrorMessage message={"Value must be between 0 and 1"} />
            ) : null}
          </Filter>
        ) : null}

        <Filter>
          <FilterTitle>Select Labeling Job</FilterTitle>
          <FilterContainer>
            <SelectCampaignLabelingJob
              campaignJobName={campaign.JobName}
              selectedCampaignLabelingJob={selectedCampaignLabelingJob}
              onChange={(labelingJob) =>
                onChangeCampaignLabelingJob(labelingJob)
              }
            />
          </FilterContainer>
        </Filter>

        {selectedCampaignLabelingJob ? (
          <>
            <Filter>
              <FilterTitle>Annotation Confidence</FilterTitle>
              <SliderContainer>
                <RangeTextInput
                  type="text"
                  error={!isValidRangeText(annotationConfidenceStartText)}
                  value={annotationConfidenceStartText}
                  onChange={(e) => {
                    setAnnotationConfidenceStartText(e.currentTarget.value);
                    const value = Number(e.currentTarget.value);
                    if (isNaN(value)) {
                      return;
                    }
                    setAnnotationConfidenceStart(value);
                  }}
                />
                <Range
                  min={0}
                  max={1}
                  defaultValue={[0, 1]}
                  tipFormatter={(value) => value}
                  step={0.01}
                  trackStyle={[{ backgroundColor: theme.blue600 }]}
                  handleStyle={[{ borderColor: theme.blue600 }]}
                  onChange={onAnnotationConfidenceChange}
                />
                <RangeTextInput
                  type="text"
                  error={!isValidRangeText(annotationConfidenceEndText)}
                  value={annotationConfidenceEndText}
                  onChange={(e) => {
                    setAnnotationConfidenceEndText(e.currentTarget.value);
                    const value = Number(e.currentTarget.value);
                    if (isNaN(value)) {
                      return;
                    }
                    setAnnotationConfidenceEnd(value);
                  }}
                />
              </SliderContainer>
            </Filter>
            <Filter>
              <FilterTitle>Annotation Date</FilterTitle>
              <DateRangeFilter
                annotationStartDate={annotationStartDate}
                setAnnotationStartDate={setAnnotationStartDate}
                annotationEndDate={annotationEndDate}
                setAnnotationEndDate={setAnnotationEndDate}
              />
            </Filter>

            {classTypes ? (
              <Filter>
                <FilterTitle>Class Types</FilterTitle>
                {classTypes.map((classType) => (
                  <Checkbox
                    checked={selectedClassTypes.has(classType)}
                    key={classType}
                    onChange={(detail) => {
                      const selectedClassTypesArray =
                        Array.from(selectedClassTypes);
                      if (detail.detail.checked) {
                        setSelectedClassTypes(
                          new Set([...selectedClassTypesArray, classType])
                        );
                      } else {
                        selectedClassTypesArray.splice(
                          selectedClassTypesArray.indexOf(classType),
                          1
                        );
                        setSelectedClassTypes(new Set(selectedClassTypesArray));
                      }
                    }}
                  >
                    {classType}
                  </Checkbox>
                ))}
              </Filter>
            ) : null}
          </>
        ) : null}
      </Container>
    </MapSidebarContainer>
  );
};

const fetchZoomInfo = async (s3TilePrefix: string) => {
  const s3TilePrefixClean = s3TilePrefix.replace(/\/$/, "");
  const s3TileMetadataUri = `${s3TilePrefixClean}/metadata.json`;
  const signedS3Url = getSignedS3UrlFromS3Uri(s3TileMetadataUri);

  try {
    const metadataResponse = await fetch(signedS3Url);
    if (metadataResponse.ok) {
      const metadataObj = await metadataResponse.json();
      const bounds = metadataObj.bounds as string | number[] | undefined;

      const realBounds =
        typeof bounds === "string" ? bounds.split(",").map(Number) : bounds;
      return {
        minzoom: parseInt(metadataObj.minzoom) || 1,
        maxzoom: parseInt(metadataObj.maxzoom) || 9,
        bounds: realBounds,
      };
    }
  } catch (e) {
    console.error("Exception raised during zoom fetch, using defaults", e);
    return {
      minzoom: 1,
      maxzoom: 9,
    };
  }
};

export const MapComponent = (props: {
  campaign: Campaign;
  selectedCampaignPrefilteringJob?: CampaignPrefilteringJob;
  onSetPrefilteringConfidenceStart?: (
    preFilteringConfidenceStart: number
  ) => void;
  prefilteringConfidenceStart?: number;
  hideSidebar?: boolean;
  height?: number;
}) => {
  const { campaign, onSetPrefilteringConfidenceStart, hideSidebar, height } =
    props;
  const inputPrefilteringConfidenceStart = props.prefilteringConfidenceStart;
  const inputSelectedCampaignPrefilteringJob =
    props.selectedCampaignPrefilteringJob;

  const [preFilteringConfidenceStart, setPreFilteringConfidenceStart] =
    useState<number>(0);
  const [preFilteringConfidenceEnd, setPreFilteringConfidenceEnd] =
    useState<number>(1);
  const [annotationConfidenceStart, setAnnotationConfidenceStart] =
    useState<number>(0);
  const [annotationConfidenceEnd, setAnnotationConfidenceEnd] =
    useState<number>(1);

  const [annotationStartDate, setAnnotationStartDate] = useState<string | null>(
    null
  );
  const [annotationEndDate, setAnnotationEndDate] = useState<string | null>(
    null
  );

  const [rasterLayerInfos, setRasterLayerInfos] = useState<
    {
      name: string;
      url: string;
      bounds: [number, number, number, number];
    }[]
  >([]);

  const [chipTileLayer, setChipTileLayer] = useState<
    TileLayerMetadata | undefined
  >();
  const [annotationTileLayer, setAnnotationTileLayer] = useState<
    TileLayerMetadata | undefined
  >();
  const [selectedCampaignPrefilteringJob, setSelectedCampaignPrefilteringJob] =
    useState<CampaignPrefilteringJob | undefined>(undefined);
  const [selectedCampaignLabelingJob, setSelectedCampaignLabelingJob] =
    useState<CampaignLabelingJob | undefined>(undefined);

  const classTypes = selectedCampaignLabelingJob?.FeatureClasses;
  const [selectedClassTypes, setSelectedClassTypes] = useState<Set<string>>(
    new Set(classTypes) || new Set<string>()
  );

  useEffect(() => {
    if (classTypes) {
      setSelectedClassTypes(new Set(classTypes));
    }
  }, [classTypes]);

  useEffect(() => {
    if (onSetPrefilteringConfidenceStart) {
      onSetPrefilteringConfidenceStart(preFilteringConfidenceStart);
    }
  }, [preFilteringConfidenceStart, onSetPrefilteringConfidenceStart]);

  useEffect(() => {
    if (
      inputPrefilteringConfidenceStart !== undefined &&
      preFilteringConfidenceStart !== inputPrefilteringConfidenceStart
    ) {
      setPreFilteringConfidenceStart(inputPrefilteringConfidenceStart);
    }
  }, [inputPrefilteringConfidenceStart, preFilteringConfidenceStart]);

  useEffect(() => {
    if (
      inputSelectedCampaignPrefilteringJob !== undefined &&
      selectedCampaignPrefilteringJob !== inputSelectedCampaignPrefilteringJob
    ) {
      setSelectedCampaignPrefilteringJob(inputSelectedCampaignPrefilteringJob);
    }
  }, [inputSelectedCampaignPrefilteringJob, selectedCampaignPrefilteringJob]);

  useEffect(() => {
    const getChipLayer = async () => {
      const prefilteringComplete =
        selectedCampaignPrefilteringJob &&
        selectedCampaignPrefilteringJob.Status === "Complete" &&
        campaign.ChipVectorTilePrefix;

      const vectorTileS3Prefix = prefilteringComplete
        ? campaign.ChipVectorTilePrefix?.replace(
            /campaign\//,
            `prefiltering/${selectedCampaignPrefilteringJob?.CampaignPrefilteringId}/`
          )
        : campaign.ChipVectorTilePrefix;
      if (!vectorTileS3Prefix) {
        return;
      }

      const vectorTileHttpsPrefix = s3UrlToHttpsUrl(vectorTileS3Prefix).replace(
        /\/$/,
        ""
      );

      setChipTileLayer({
        tileUrls: [`${vectorTileHttpsPrefix}/{z}/{x}/{y}.pbf`],
        ...(await fetchZoomInfo(vectorTileS3Prefix)),
      });
    };

    getChipLayer();
  }, [campaign, selectedCampaignPrefilteringJob]);

  useEffect(() => {
    const getAnnotationLayer = async () => {
      if (selectedCampaignLabelingJob && campaign.ChipVectorTilePrefix) {
        const vectorTilePrefix = `${campaign.OutputDataS3Prefix}labeling_job/${selectedCampaignLabelingJob.GroundTruthLabelingJobName}/pbf`;
        const vectorTileHttpsPrefix = s3UrlToHttpsUrl(vectorTilePrefix).replace(
          /\/$/,
          ""
        );
        setAnnotationTileLayer({
          tileUrls: [`${vectorTileHttpsPrefix}/{z}/{x}/{y}.pbf`],
          ...(await fetchZoomInfo(vectorTilePrefix)),
        });
      }
    };
    getAnnotationLayer();
  }, [campaign, selectedCampaignLabelingJob]);

  useEffect(() => {
    const myRasterLayers: {
      name: string;
      url: string;
      bounds: [number, number, number, number];
    }[] = [];
    if (!campaign.RasterTileS3Prefixes) {
      return;
    }
    for (let i = 0; i < campaign.RasterTileS3Prefixes.length; i++) {
      const s3Prefix = campaign.RasterTileS3Prefixes[i];
      const fileInfo = campaign.Files[i];
      if (!fileInfo) {
        console.log("No fileInfo found for raster tile prefix ", s3Prefix);
        break;
      }
      const httpsPrefix = s3UrlToHttpsUrl(s3Prefix).replace(/\/$/, "");
      const name = httpsPrefix.split("/").pop() || "Layer";
      const swLng = fileInfo.Extent[1][0];
      const swLat = fileInfo.Extent[1][1];
      const neLng = fileInfo.Extent[3][0];
      const neLat = fileInfo.Extent[3][1];
      myRasterLayers.push({
        name,
        url: `${httpsPrefix}/{z}/{x}/{y}.png`,
        bounds: [swLng, swLat, neLng, neLat],
      });
    }
    setRasterLayerInfos(myRasterLayers);
  }, [campaign]);

  const sourceFileNames = campaign.Files.map((fileInfo) => {
    const imageName = fileInfo.Filename.split(".")[0];
    return imageName;
  });
  if (campaign.Files && campaign.Files.length > 0) {
    if (campaign.Files[0].Center && campaign.Files[0].Center.length > 1) {
      mapCenter[0] = campaign.Files[0].Center[0];
      mapCenter[1] = campaign.Files[0].Center[1];
    }
  }
  const annotationLayerName = "annotations";
  const popupLayers = [
    annotationLayerName,
    ...sourceFileNames.map((n) => n.replace(/[.-]/, "")),
  ];

  const renderTitle = (feature: maplibregl.MapboxGeoJSONFeature) => {
    if (feature.sourceLayer === annotationLayerName) {
      const name =
        feature.properties &&
        (feature.properties["class"] || feature.properties["class_name"])
          ? `Object: ${
              feature.properties["class"] || feature.properties["class_name"]
            }`
          : "Object";
      return `${name}`;
    } else {
      return `Image Chip: ${feature.sourceLayer}`;
    }
  };
  const renderSubtitle = (feature: maplibregl.MapboxGeoJSONFeature) => {
    if (feature.sourceLayer === annotationLayerName) {
      return "Worker Annotations";
    } else {
      return "Chip Boundaries";
    }
  };
  return (
    <CampaignMapContainer>
      {hideSidebar ? null : (
        <MapSidebar
          height={height}
          campaign={campaign}
          selectedCampaignLabelingJob={selectedCampaignLabelingJob}
          onChangeCampaignLabelingJob={setSelectedCampaignLabelingJob}
          selectedCampaignPrefilteringJob={selectedCampaignPrefilteringJob}
          onChangeCampaignPrefilteringJob={setSelectedCampaignPrefilteringJob}
          onChangePrefilteringConfidenceStart={setPreFilteringConfidenceStart}
          onChangePrefilteringConfidenceEnd={setPreFilteringConfidenceEnd}
          annotationConfidenceStart={annotationConfidenceStart}
          setAnnotationConfidenceStart={setAnnotationConfidenceStart}
          annotationConfidenceEnd={annotationConfidenceEnd}
          setAnnotationConfidenceEnd={setAnnotationConfidenceEnd}
          preFilteringConfidenceStart={preFilteringConfidenceStart}
          setPreFilteringConfidenceStart={(value) => {
            setPreFilteringConfidenceStart(value);
          }}
          preFilteringConfidenceEnd={preFilteringConfidenceEnd}
          setPreFilteringConfidenceEnd={setPreFilteringConfidenceEnd}
          annotationStartDate={annotationStartDate}
          setAnnotationStartDate={setAnnotationStartDate}
          annotationEndDate={annotationEndDate}
          setAnnotationEndDate={setAnnotationEndDate}
          selectedClassTypes={selectedClassTypes}
          setSelectedClassTypes={setSelectedClassTypes}
        />
      )}

      <MapContainer height={height}>
        <Map center={mapCenter} styleUrl={styleUrl} height={height}>
          <RasterLayer rasterLayerInfos={rasterLayerInfos} />
          <VectorLayer
            namePrefixes={sourceFileNames}
            chipTile={chipTileLayer}
            annotationTile={annotationTileLayer}
            annotationLayerName={annotationLayerName}
            annotationConfidenceStart={annotationConfidenceStart}
            annotationConfidenceEnd={annotationConfidenceEnd}
            preFilteringConfidenceStart={preFilteringConfidenceStart}
            preFilteringConfidenceEnd={preFilteringConfidenceEnd}
            annotationStartDate={annotationStartDate}
            annotationEndDate={annotationEndDate}
            selectedClassTypes={selectedClassTypes}
          />
          <MapPopup
            layerNames={popupLayers}
            renderTitle={renderTitle}
            renderSubtitle={renderSubtitle}
          />
        </Map>
      </MapContainer>
    </CampaignMapContainer>
  );
};

const CampaignMap = (props: { campaign: Campaign }) => {
  const { campaign } = props;

  return (
    <ContentSection>
      <Container header={<Header variant="h2">Map</Header>}>
        <MapComponent campaign={campaign} />
      </Container>
    </ContentSection>
  );
};
export default CampaignMap;
